import { useCallback, useMemo, useReducer } from 'react';

/** 믹스 패드·사각 버튼과 대응하는 사운드 키 */
export type MixSoundId = 'rain' | 'bird' | 'wind' | 'forest' | 'valley' | 'flight';

/** 전체 사운드 id (표시 순서 고정) */
export const ALL_MIX_SOUND_IDS: readonly MixSoundId[] = [
  'rain',
  'bird',
  'wind',
  'forest',
  'valley',
  'flight',
];

/** 랜딩 초기값: 빗소리·새소리만 켜짐 */
export const DEFAULT_ACTIVE_MIX_SOUND_IDS: readonly MixSoundId[] = ['rain', 'bird'] as const;

/** 패드 원 지름(%) — y 클램프·레이아웃과 동기 */
export const MIX_PAD_DIAMETER_PCT: Record<MixSoundId, number> = {
  rain: 24,
  bird: 14.5,
  wind: 16,
  forest: 16,
  valley: 15,
  flight: 14,
};

/** 패드 기준 초기 y 중심(%, 상단 0) — 상태의 단일 출처 */
export const MIX_PAD_DEFAULT_YPCT: Record<MixSoundId, number> = {
  rain: 8,
  bird: 46,
  wind: 58,
  forest: 39,
  valley: 72,
  flight: 20,
};

export type MixSoundYEntry = { id: MixSoundId; yPct: number };

export function createDefaultActiveMixSoundSet(): Set<MixSoundId> {
  return new Set(DEFAULT_ACTIVE_MIX_SOUND_IDS);
}

/** 처음부터 모두 켜진 Set */
export function createAllActiveMixSoundSet(): Set<MixSoundId> {
  return new Set(ALL_MIX_SOUND_IDS);
}

/**
 * 액티브 Set만 갱신 (on → off: 제거, off → on: 추가)
 * React state용으로 항상 새 Set 인스턴스 반환
 */
export function toggleMixSoundInSet(active: ReadonlySet<MixSoundId>, id: MixSoundId): Set<MixSoundId> {
  const next = new Set(active);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/** y 중심 0~100%. 원 지름의 절반까지 패드 밖으로 나갈 수 있음(지름 기준 50% 돌출). */
export function clampMixPadYPct(yPct: number, _id: MixSoundId, _diameterPct?: number): number {
  return Math.min(100, Math.max(0, yPct));
}

function buildInitialYPct(): Record<MixSoundId, number> {
  return { ...MIX_PAD_DEFAULT_YPCT };
}

/** z-index: 앞쪽이 뒤, 맨 뒤가 최상단 */
function bringIdToEndOfStack(stack: MixSoundId[], id: MixSoundId): MixSoundId[] {
  return [...stack.filter((x) => x !== id), id];
}

type MixPadReducerState = {
  activeSet: Set<MixSoundId>;
  /** 그리기 순서(앞→뒤). 마지막 요소가 최상단 z-index */
  zStack: MixSoundId[];
  yPctBySound: Record<MixSoundId, number>;
};

type MixPadAction =
  | { type: 'toggle'; id: MixSoundId }
  | { type: 'setYPct'; id: MixSoundId; yPct: number; diameterPct?: number }
  | { type: 'bringToFront'; id: MixSoundId }
  | { type: 'resetAllOn' }
  | { type: 'setActiveSet'; activeSet: Set<MixSoundId> };

function mixPadReducer(state: MixPadReducerState, action: MixPadAction): MixPadReducerState {
  switch (action.type) {
    case 'toggle': {
      const { id } = action;
      const prevOn = state.activeSet.has(id);
      const activeSet = toggleMixSoundInSet(state.activeSet, id);
      const nowOn = activeSet.has(id);
      let zStack = state.zStack;
      if (nowOn && !prevOn) {
        zStack = bringIdToEndOfStack(zStack, id);
      } else if (!nowOn && prevOn) {
        zStack = zStack.filter((x) => x !== id);
      }
      return { ...state, activeSet, zStack };
    }
    case 'setYPct': {
      const y = clampMixPadYPct(action.yPct, action.id, action.diameterPct);
      return {
        ...state,
        yPctBySound: { ...state.yPctBySound, [action.id]: y },
      };
    }
    case 'bringToFront': {
      if (!state.activeSet.has(action.id)) return state;
      return {
        ...state,
        zStack: bringIdToEndOfStack(state.zStack, action.id),
      };
    }
    case 'resetAllOn':
      return {
        ...state,
        activeSet: createAllActiveMixSoundSet(),
        zStack: [...ALL_MIX_SOUND_IDS],
      };
    case 'setActiveSet': {
      const activeSet = new Set(action.activeSet);
      const zStack = ALL_MIX_SOUND_IDS.filter((id) => activeSet.has(id));
      return { ...state, activeSet, zStack };
    }
    default:
      return state;
  }
}

function initialMixPadState(initialActive: Iterable<MixSoundId>): MixPadReducerState {
  return {
    activeSet: new Set(initialActive),
    zStack: [...ALL_MIX_SOUND_IDS],
    yPctBySound: buildInitialYPct(),
  };
}

/**
 * 액티브 Set + 패드 y(%) + z-스택(마지막 터치가 최상단)
 */
export function useMixActiveSounds(initialActive: Iterable<MixSoundId> = DEFAULT_ACTIVE_MIX_SOUND_IDS) {
  const [state, dispatch] = useReducer(mixPadReducer, initialActive, initialMixPadState);

  const { activeSet, zStack, yPctBySound } = state;

  const activeList = useMemo(
    () => ALL_MIX_SOUND_IDS.filter((id) => activeSet.has(id)),
    [activeSet],
  );

  /** `ALL_MIX_SOUND_IDS` 순서로 id·y% 리스트 (연동·로그용) */
  const soundYList = useMemo(
    (): MixSoundYEntry[] => ALL_MIX_SOUND_IDS.map((id) => ({ id, yPct: yPctBySound[id] })),
    [yPctBySound],
  );

  const isActive = useCallback((id: MixSoundId) => activeSet.has(id), [activeSet]);

  const toggle = useCallback((id: MixSoundId) => {
    dispatch({ type: 'toggle', id });
  }, []);

  const setSoundYPct = useCallback((id: MixSoundId, yPct: number, diameterPct?: number) => {
    dispatch({ type: 'setYPct', id, yPct, diameterPct });
  }, []);

  const bringPadSoundToFront = useCallback((id: MixSoundId) => {
    dispatch({ type: 'bringToFront', id });
  }, []);

  const resetAllOn = useCallback(() => {
    dispatch({ type: 'resetAllOn' });
  }, []);

  const setActiveSet = useCallback((next: Set<MixSoundId>) => {
    dispatch({ type: 'setActiveSet', activeSet: next });
  }, []);

  return {
    activeSet,
    activeList,
    soundYList,
    yPctBySound,
    zStack,
    isActive,
    toggle,
    setSoundYPct,
    bringPadSoundToFront,
    resetAllOn,
    setActiveSet,
  };
}
